import { apiClient } from "lib/apiClient";

/* ---------- Customer-360 sources ---------- */
//
// TWO picard endpoints, deliberately:
//
//   1. users/get-users-data/       -> identity, family, engagement (the User Data screen's
//                                     own source, so the panel and the full page agree)
//   2. subscription/subscriptions/ -> the subscription, with REAL tz-aware datetimes
//
// Why not take the subscription from get-users-data, which also returns one?
// Because it is WRONG BY A DAY. `get_user_data` (users/common_fn.py:437) builds
// `date_expired` as `s_end_time.date()` — `.date()` on a tz-AWARE datetime truncates in
// UTC, not IST. A subscription ending 2026-07-13T04:41+05:30 (= 2026-07-12T23:11Z) comes
// back as "2026-07-12" and renders as "expires today" when the queue — which reads the
// task payload's IST datetime — correctly says "1 day left to save".
// A panel that contradicts the row it opened from is worse than no panel, so the
// subscription facts come from the endpoint that still has the time on them.
// (The truncation is picard's bug; it is not ours to fix from here.)

/* ---------- 1. users/get-users-data/ ---------- */
//
// POST (not GET) — body is {mobile} or {user_id}. There is NO serializer behind it; the
// response is a hand-built dict (users/views.py:621 -> users/common_fn.py:367). Every
// field below was read off that function and verified against the live payload.
// A miss is NOT a 404: picard answers 200 with {"messages": "Number not found"}.

/** patients[] — common_fn.py:476. `relation` exists on the model but is NOT serialized. */
export interface CustomerPatient {
  id?: number | null;
  name?: string | null;
  gender?: string | null;
  date_of_birth?: string | null;
  /** "<birth_weight> kg" or the literal string "NA" */
  weight?: string | null;
}

/** appointment[] — common_fn.py:499. No id and no doctor name on this shape. */
export interface CustomerAppointment {
  type?: string | null;
  appointment_time?: string | null;
  status?: string | null;
}

export interface CustomerData {
  user_id?: number | null;
  user_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  /** date only (Account.date_joined.date()) — this is "member since" */
  user_joining_date?: string | null;
  total_messages?: number | null;
  total_questions?: number | null;
  current_groups?: string[] | null;
  patients?: CustomerPatient[] | null;
  appointment?: CustomerAppointment[] | null;
}

/** The "not found" arm of the same 200 response. */
interface CustomerMiss {
  messages?: string;
}

/**
 * `null` = picard has no record for this number (a real, expected answer — not an
 * error). Anything else that goes wrong throws, so TanStack Query surfaces Retry.
 */
export async function fetchCustomer(mobile: string): Promise<CustomerData | null> {
  const digits = mobile.replace(/\D/g, "");
  if (!digits) return null;
  const { data } = await apiClient.post<CustomerData & CustomerMiss>(
    "users/get-users-data/",
    { mobile: digits }
  );
  if (data?.messages === "Number not found") return null;
  return data;
}

/* ---------- 2. subscription/subscriptions/ ---------- */
// GET, paginated (?mobile=). SubscriptionSerializer (subscription/serializers.py:7).

export interface Subscription {
  id?: number | null;
  plan_name?: string | null;
  /** tz-aware ISO datetimes — the whole reason we use this endpoint */
  s_start_time?: string | null;
  s_end_time?: string | null;
  /**
   * Call utilisation. REAL columns (Subscription.max_calls / used_calls), both nullable.
   * Nothing clamps used <= max.
   */
  max_calls?: number | null;
  used_calls?: number | null;
  is_active?: boolean | null;
}

interface SubscriptionsResponse {
  results?: Subscription[];
}

export async function fetchSubscriptions(mobile: string): Promise<Subscription[]> {
  const digits = mobile.replace(/\D/g, "");
  if (!digits) return [];
  const { data } = await apiClient.get<SubscriptionsResponse>(
    "subscription/subscriptions/",
    { params: { mobile: digits } }
  );
  return data?.results ?? [];
}

/**
 * The subscription the CM is being asked about: the live one, else the most recently
 * ended (a lapsed customer's LAST plan is the one you are trying to win back).
 */
export function pickSubscription(subs: Subscription[]): Subscription | null {
  if (subs.length === 0) return null;
  const active = subs.find((s) => s.is_active);
  if (active) return active;
  return [...subs].sort(
    (a, b) => Date.parse(b.s_end_time ?? "") - Date.parse(a.s_end_time ?? "")
  )[0];
}

/* ---------- derivations ---------- */

const MS_PER_DAY = 86_400_000;

/** "14 Jun 2026" — the panel has room for the year; the row does not. */
export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return new Date(t).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Age from DOB. Babynama's customers are parents of INFANTS — a "0 yr" child is the
 * norm, so months carry the clinical signal and must never round away.
 */
export function formatAge(dob: string | null | undefined, now: number): string | null {
  if (!dob) return null;
  const t = Date.parse(dob);
  if (Number.isNaN(t) || t > now) return null;

  const birth = new Date(t);
  const today = new Date(now);
  let months =
    (today.getFullYear() - birth.getFullYear()) * 12 +
    (today.getMonth() - birth.getMonth());
  if (today.getDate() < birth.getDate()) months -= 1;
  if (months < 0) months = 0;

  if (months < 1) {
    const days = Math.max(0, Math.floor((now - t) / MS_PER_DAY));
    return `${days} day${days === 1 ? "" : "s"} old`;
  }
  if (months < 24) return `${months} mo`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem ? `${years} yr ${rem} mo` : `${years} yr`;
}

/**
 * Days until expiry; negative = already lapsed. Same `ceil` as the queue's own P2 maths
 * (priority.ts) so the panel and the row can never disagree by a day.
 */
export function daysUntil(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - now) / MS_PER_DAY);
}

export interface CallUsage {
  used: number;
  max: number;
  /** 0–100, clamped — `used` is not clamped to `max` upstream. */
  pct: number;
  /** A paid-for plan going unused is the loudest churn tell we have. */
  underused: boolean;
}

/**
 * Only returns usage when picard actually has both counters. They are nullable, so a
 * plan with no quota on file must render "not on file", never a fake "0 / 0".
 */
export function callUsage(sub: Subscription | null): CallUsage | null {
  if (!sub) return null;
  const max = sub.max_calls;
  const used = sub.used_calls;
  if (typeof max !== "number" || typeof used !== "number" || max <= 0) return null;
  const pct = Math.min(100, Math.max(0, Math.round((used / max) * 100)));
  return { used, max, pct, underused: pct <= 50 };
}
